import React, { Component } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const SUMMARY_URL = "/mc-recording/web/user-recordings-stats-summary";
const DETAIL_URL = "/mc-recording/web/user-recordings-with-stats";

export default class UserRecordingsWithStatsPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            uidsInput: "",
            startDate: "",
            endDate: "",
            loading: false,
            result: null, // summary: { grandTotal, results: [{ uid, userId, userName, total, recordings }] }
            expanded: {}, // uid -> true nếu đang mở
            loaded: {}, // uid -> true nếu đã load chi tiết bài
            loadingUid: null, // uid đang load chi tiết
            lastRange: null, // { startMillis, endMillis } để load chi tiết từng user
        };
    }

    handleChange = (field) => (e) => {
        this.setState({ [field]: e.target.value });
    };

    parseUids = (rawValue) => {
        if (!rawValue) return [];
        const items = rawValue
            .split(/\r?\n|,|;|\s+/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
        return Array.from(new Set(items));
    };

    handleSubmit = () => {
        const { uidsInput, startDate, endDate } = this.state;
        const uids = this.parseUids(uidsInput);

        if (uids.length === 0) {
            toast.warning("Vui lòng nhập ít nhất 1 uid");
            return;
        }
        if (!startDate || !endDate) {
            toast.warning("Vui lòng chọn ngày bắt đầu và kết thúc");
            return;
        }
        if (startDate > endDate) {
            toast.warning("Ngày bắt đầu phải trước hoặc bằng ngày kết thúc");
            return;
        }

        // startDate lấy từ 00:00:00, endDate lấy hết ngày (23:59:59.999) để bao trọn khoảng
        const startMillis = new Date(`${startDate}T00:00:00`).getTime();
        const endMillis = new Date(`${endDate}T23:59:59.999`).getTime();

        this.setState({ loading: true, result: null, expanded: {}, loaded: {} });

        axios
            .post(
                `${global.config.apiDomain}${SUMMARY_URL}`,
                { uids, startDate: startMillis, endDate: endMillis },
                { headers: { "Content-Type": "application/json" } }
            )
            .then((res) => {
                const responseData = res?.data;
                if (responseData?.status !== "OK") {
                    toast.error(responseData?.message || "Không thể lấy thống kê bài thu");
                    this.setState({ loading: false });
                    return;
                }
                this.setState({
                    loading: false,
                    result: responseData.data || null,
                    lastRange: { startMillis, endMillis },
                });
            })
            .catch((error) => {
                const errorMessage =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Không thể lấy thống kê bài thu";
                toast.error(errorMessage);
                this.setState({ loading: false });
            });
    };

    // Load chi tiết bài của 1 user (gọi API chỉ với uid đó → backend trả đầy đủ, không cap)
    loadDetail = (uid) => {
        const { lastRange } = this.state;
        if (!lastRange) return;

        this.setState({ loadingUid: uid });

        axios
            .post(
                `${global.config.apiDomain}${DETAIL_URL}`,
                { uids: [uid], startDate: lastRange.startMillis, endDate: lastRange.endMillis },
                { headers: { "Content-Type": "application/json" } }
            )
            .then((res) => {
                const responseData = res?.data;
                if (responseData?.status !== "OK") {
                    toast.error(responseData?.message || "Không thể tải chi tiết bài thu");
                    this.setState({ loadingUid: null });
                    return;
                }
                const fullGroup = responseData.data?.results?.[0];
                this.setState((prev) => {
                    if (!fullGroup || !prev.result) return { loadingUid: null };
                    const results = prev.result.results.map((g) =>
                        g.uid === uid ? { ...g, recordings: fullGroup.recordings, total: fullGroup.total } : g
                    );
                    return {
                        loadingUid: null,
                        result: { ...prev.result, results },
                        loaded: { ...prev.loaded, [uid]: true },
                    };
                });
            })
            .catch((error) => {
                const errorMessage =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Không thể tải chi tiết bài thu";
                toast.error(errorMessage);
                this.setState({ loadingUid: null });
            });
    };

    toggleGroup = (group) => {
        const uid = group.uid;
        const willOpen = !this.state.expanded[uid];
        this.setState((prev) => ({ expanded: { ...prev.expanded, [uid]: willOpen } }));

        // Lần đầu mở + có bài + chưa load → lazy load chi tiết
        if (willOpen && group.userId && (group.total ?? 0) > 0 && !this.state.loaded[uid]) {
            this.loadDetail(uid);
        }
    };

    collapseAll = () => {
        this.setState({ expanded: {} });
    };

    // Copy id + tên + số bài dạng TSV để paste vào Google Sheet
    copyTsv = () => {
        const { result } = this.state;
        if (!result || !Array.isArray(result.results) || result.results.length === 0) {
            toast.warning("Chưa có dữ liệu để copy");
            return;
        }

        const header = ["ID", "Tên", "Số bài"].join("\t");
        const rows = result.results.map((g) =>
            [g.userId || g.uid, g.userName || "", g.total ?? 0].join("\t")
        );
        const tsv = [header, ...rows].join("\n");

        const done = () => toast.success(`Đã copy ${rows.length} dòng (TSV)`);
        const fail = () => toast.error("Không thể copy vào clipboard");

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(tsv).then(done).catch(fail);
        } else {
            // Fallback cho trình duyệt cũ / không có clipboard API
            try {
                const textarea = document.createElement("textarea");
                textarea.value = tsv;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
                done();
            } catch (e) {
                fail();
            }
        }
    };

    formatDate = (millis) => {
        if (!millis) return "-";
        try {
            return new Date(millis).toLocaleString();
        } catch (e) {
            return "-";
        }
    };

    renderTable(recordings) {
        return (
            <div className="table-responsive" style={{ maxHeight: "420px", overflowY: "auto" }}>
                <table className="table table-hover table-bordered table-sm mb-0">
                    <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                        <tr>
                            <th style={{ width: "50px" }}>#</th>
                            <th style={{ width: "150px" }}>Recording ID</th>
                            <th>Tên bài</th>
                            <th style={{ width: "100px" }}>Loại</th>
                            <th style={{ width: "80px" }}>Like</th>
                            <th style={{ width: "90px" }}>Comment</th>
                            <th style={{ width: "80px" }}>Share</th>
                            <th style={{ width: "170px" }}>Ngày thu</th>
                            <th style={{ width: "70px" }}>Video</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recordings.map((item, idx) => (
                            <tr key={item.id ?? idx}>
                                <td>{idx + 1}</td>
                                <td>{item.id ?? "-"}</td>
                                <td>{item.songName || "-"}</td>
                                <td>{item.performanceType || "-"}</td>
                                <td>{item.noLike ?? 0}</td>
                                <td>{item.noComment ?? 0}</td>
                                <td>{item.noShare ?? 0}</td>
                                <td>{this.formatDate(item.recordingTime || item.addTime)}</td>
                                <td>
                                    {item.mixedVideoUrl ? (
                                        <a href={item.mixedVideoUrl} target="_blank" rel="noopener noreferrer">
                                            Xem
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderGroup(group, index) {
        const { expanded, loaded, loadingUid } = this.state;
        const notFound = !group.userId;
        const total = group.total ?? 0;
        const isOpen = !!expanded[group.uid];
        const isLoadingThis = loadingUid === group.uid;
        const isLoaded = !!loaded[group.uid];
        const recordings = Array.isArray(group.recordings) ? group.recordings : [];

        return (
            <div className="card mt-2" key={group.uid ?? index}>
                <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ cursor: notFound ? "default" : "pointer" }}
                    onClick={notFound ? undefined : () => this.toggleGroup(group)}
                >
                    <span>
                        {!notFound && (
                            <i className={`fa-solid ${isOpen ? "fa-chevron-down" : "fa-chevron-right"} mr-2`}></i>
                        )}
                        <strong>UID {group.uid}</strong>
                        {group.userName ? ` — ${group.userName}` : ""}
                        {group.userId ? (
                            <span className="text-muted"> (id: {group.userId})</span>
                        ) : (
                            <span className="badge badge-warning ml-2">Không tìm thấy user</span>
                        )}
                    </span>
                    <span className="badge badge-primary">{total} bài</span>
                </div>

                {!notFound && isOpen && (
                    <>
                        {isLoadingThis && (
                            <div className="card-body text-muted">
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Đang tải chi tiết...
                            </div>
                        )}
                        {!isLoadingThis && total === 0 && (
                            <div className="card-body text-muted">
                                Không có bài thu public nào trong khoảng thời gian đã chọn.
                            </div>
                        )}
                        {!isLoadingThis && total > 0 && isLoaded && this.renderTable(recordings)}
                    </>
                )}
            </div>
        );
    }

    renderResult() {
        const { result } = this.state;
        if (!result || !Array.isArray(result.results)) return null;

        return (
            <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="alert alert-info mb-2 mr-2 flex-grow-1">
                        <strong>Tổng số bài thu (tất cả user):</strong> {result.grandTotal ?? 0}
                        <span className="text-muted ml-2">— {result.results.length} user</span>
                    </div>
                    <div className="mb-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success mr-2"
                            onClick={this.copyTsv}
                        >
                            <i className="fa-solid fa-copy mr-1"></i>
                            Copy (ID, Tên, Số bài)
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={this.collapseAll}
                        >
                            Đóng tất cả
                        </button>
                    </div>
                </div>
                {result.results.map((group, index) => this.renderGroup(group, index))}
            </div>
        );
    }

    render() {
        const { uidsInput, startDate, endDate, loading } = this.state;

        return (
            <div className="container-fluid" style={{ marginTop: "100px", marginBottom: "20px" }}>
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-header bg-primary text-white">
                                <h3 className="card-title mb-0">
                                    <i className="fa-solid fa-music mr-2"></i>
                                    Thống kê bài thu của user
                                </h3>
                            </div>
                            <div className="card-body">
                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label htmlFor="uids">
                                            <strong>Danh sách UID / User.id</strong>
                                        </label>
                                        <textarea
                                            id="uids"
                                            className="form-control"
                                            rows="5"
                                            value={uidsInput}
                                            onChange={this.handleChange("uidsInput")}
                                            placeholder="Mỗi dòng 1 uid (số) hoặc User.id, hoặc tách bằng dấu phẩy/khoảng trắng"
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group col-md-3">
                                        <label htmlFor="startDate">
                                            <strong>Từ ngày</strong>
                                        </label>
                                        <input
                                            id="startDate"
                                            type="date"
                                            className="form-control"
                                            value={startDate}
                                            onChange={this.handleChange("startDate")}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group col-md-3">
                                        <label htmlFor="endDate">
                                            <strong>Đến ngày</strong>
                                        </label>
                                        <input
                                            id="endDate"
                                            type="date"
                                            className="form-control"
                                            value={endDate}
                                            onChange={this.handleChange("endDate")}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={this.handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                            Đang tải...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-magnifying-glass mr-2"></i>
                                            Tìm kiếm
                                        </>
                                    )}
                                </button>

                                {this.renderResult()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
